/**
 * @typedef {Object} ProductionOrderCreateDto
 * @property {string} orderNumber
 * @property {string} [orderType]
 * @property {number} statusId
 * @property {number} warehouseId
 * @property {string} [plannedStartDate]
 * @property {string} [plannedEndDate]
 * @property {string} [actualStartDate]
 * @property {string} [actualEndDate]
 * @property {string} [externalSystemCode]
 * @property {string} [externalOrderNumber]
 * @property {string} [externalReference]
 * @property {string} [notes]
 */

/**
 * @typedef {Object} ProductionOrderUpdateDto
 * @property {string} [orderNumber]
 * @property {string} [orderType]
 * @property {number} [statusId]
 * @property {number} [warehouseId]
 * @property {string} [plannedStartDate]
 * @property {string} [plannedEndDate]
 * @property {string} [actualStartDate]
 * @property {string} [actualEndDate]
 * @property {string} [externalSystemCode]
 * @property {string} [externalOrderNumber]
 * @property {string} [externalReference]
 * @property {string} [notes]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} ProductionOrderResponseDto
 * @property {number} productionOrderId
 * @property {string} orgId
 * @property {string} orderNumber
 * @property {string} [orderType]
 * @property {number} statusId
 * @property {string} statusName
 * @property {number} warehouseId
 * @property {string} warehouseName
 * @property {string} [plannedStartDate]
 * @property {string} [plannedEndDate]
 * @property {string} [actualStartDate]
 * @property {string} [actualEndDate]
 * @property {string} [externalSystemCode]
 * @property {string} [externalOrderNumber]
 * @property {string} [externalReference]
 * @property {string} [notes]
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 * @property {number} lineCount
 */

/**
 * @typedef {Object} ProductionOrderListResponse
 * @property {ProductionOrderResponseDto[]} orders
 * @property {number} totalCount
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {number} totalPages
 */

/**
 * @typedef {Object} ProductionOrderLineCreateDto
 * @property {number} componentId
 * @property {number} recipeVersionId
 * @property {number} routeId
 * @property {number} quantityOrdered
 * @property {number} [statusId]
 */

/**
 * @typedef {Object} ProductionOrderLineUpdateDto
 * @property {number} [componentId]
 * @property {number} [recipeVersionId]
 * @property {number} [routeId]
 * @property {number} [quantityOrdered]
 * @property {number} [quantityCompleted]
 * @property {number} [statusId]
 */

/**
 * @typedef {Object} ProductionOrderLineResponseDto
 * @property {number} orderLineId
 * @property {number} productionOrderId
 * @property {number} lineNumber
 * @property {number} componentId
 * @property {string} componentName
 * @property {string} componentCode
 * @property {number} recipeVersionId
 * @property {string} recipeVersionName
 * @property {number} routeId
 * @property {string} routeName
 * @property {number} quantityOrdered
 * @property {number} quantityCompleted
 * @property {number} statusId
 * @property {string} statusName
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 */

/**
 * @typedef {Object} ProductionOrderStatusDto
 * @property {number} statusId
 * @property {string} statusName
 * @property {string} statusCode
 */

const BASE_PATH = 'v1/ProductionOrder';

export default function (instance) {
  return {
    /**
     * Get all production orders with pagination
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive=false]
     * @param {number} [params.pageNumber=1]
     * @param {number} [params.pageSize=50]
     * @returns {Promise<{data: ProductionOrderListResponse}>}
     */
    getOrders(params = {}) {
      return instance.get(BASE_PATH, { params });
    },

    /**
     * Get production order by ID
     * @param {number} id
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    getOrder(id) {
      return instance.get(`${BASE_PATH}/${id}`);
    },

    /**
     * Create new production order
     * @param {ProductionOrderCreateDto} payload
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    createOrder(payload) {
      return instance.post(BASE_PATH, payload);
    },

    /**
     * Update production order
     * @param {number} id
     * @param {ProductionOrderUpdateDto} payload
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    updateOrder(id, payload) {
      return instance.put(`${BASE_PATH}/${id}`, payload);
    },

    /**
     * Delete production order (soft delete)
     * @param {number} id
     * @returns {Promise<void>}
     */
    deleteOrder(id) {
      return instance.delete(`${BASE_PATH}/${id}`);
    },

    /**
     * Get production order lines
     * @param {number} orderId
     * @returns {Promise<{data: ProductionOrderLineResponseDto[]}>}
     */
    getLines(orderId) {
      return instance.get(`${BASE_PATH}/${orderId}/lines`);
    },

    /**
     * Add line to production order
     * @param {number} orderId
     * @param {ProductionOrderLineCreateDto} payload
     * @returns {Promise<{data: ProductionOrderLineResponseDto}>}
     */
    createLine(orderId, payload) {
      return instance.post(`${BASE_PATH}/${orderId}/lines`, payload);
    },

    /**
     * Update production order line
     * @param {number} orderId
     * @param {number} lineId
     * @param {ProductionOrderLineUpdateDto} payload
     * @returns {Promise<{data: ProductionOrderLineResponseDto}>}
     */
    updateLine(orderId, lineId, payload) {
      return instance.put(`${BASE_PATH}/${orderId}/lines/${lineId}`, payload);
    },

    /**
     * Delete production order line (soft delete)
     * @param {number} orderId
     * @param {number} lineId
     * @returns {Promise<void>}
     */
    deleteLine(orderId, lineId) {
      return instance.delete(`${BASE_PATH}/${orderId}/lines/${lineId}`);
    },

    /**
     * Get delete preview (counts of child records to be removed)
     * @param {number} id
     * @returns {Promise<{data: {orderNumber: string, lineCount: number, wipItemCount: number, stepExecCount: number, outputCount: number, birthCfgCount: number}}>}
     */
    getDeletePreview(id) {
      return instance.get(`${BASE_PATH}/${id}/delete-preview`);
    },

    /**
     * Get all production order statuses
     * @returns {Promise<{data: ProductionOrderStatusDto[]}>}
     */
    getStatuses() {
      return instance.get(`${BASE_PATH}/statuses`);
    },

    /**
     * Get a suggested, currently-unused order number
     * @returns {Promise<{data: {orderNumber: string}}>}
     */
    getNextOrderNumber() {
      return instance.get(`${BASE_PATH}/next-order-number`);
    }
  };
}
